import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import {
  Box, Chip, Drawer, Typography, Button, Alert, IconButton,
  List, ListItem, ListItemText, Divider, GlobalStyles,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import LocationOffIcon from '@mui/icons-material/LocationOff';
import { MapContainer, TileLayer, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet.markercluster';
import 'leaflet.markercluster/dist/MarkerCluster.css';
import 'leaflet.markercluster/dist/MarkerCluster.Default.css';
import { useUser } from '../context/UserContext';
import { BACKEND_URL } from '../utils/constants';
import { colors } from '../theme';

import { ConstructionLoader } from '../components/ConstructionLoader';
// --------------------------------------------------------------------------- //
// Types
// --------------------------------------------------------------------------- //
interface ModelTag {
  id: string;
  tag_name: string;
  tag_color?: string | null;
  status: string;
  created_at?: string | null;
}
interface Deal {
  id: string;
  name: string;
  street_address?: string | null;
  city?: string | null;
  state?: string | null;
  zip_code?: string | null;
  latitude: number | null;
  longitude: number | null;
  geocode_status: string | null;
  // UserModelVersion id - what /edit-model/:id expects. Distinct from `id`,
  // which is the UserModel id used by /models/:id.
  version_id?: string | null;
  levered_irr?: string | null;
  levered_moic?: string | null;
  model_type?: string | null;
  active: boolean;
  model_tags: ModelTag[];
}
interface StageDef { name: string; color: string | null; sort_order: number; }

const UNTAGGED_KEY = '__untagged__';
const GRAY = colors.grey[400];
const US_CENTER: [number, number] = [39.8283, -98.5795];

// --------------------------------------------------------------------------- //
// Small helpers
// --------------------------------------------------------------------------- //
const hashColor = (s: string): string => {
  let hash = 0;
  for (let i = 0; i < s.length; i++) hash = s.charCodeAt(i) + ((hash << 5) - hash);
  return `hsl(${Math.abs(hash) % 360}, 55%, 50%)`;
};

const readableText = (bg: string): string => {
  const hex = bg.replace('#', '');
  if (hex.length !== 6) return colors.white; // hsl fallbacks read fine on white
  const r = parseInt(hex.slice(0, 2), 16);
  const g = parseInt(hex.slice(2, 4), 16);
  const b = parseInt(hex.slice(4, 6), 16);
  return (0.299 * r + 0.587 * g + 0.114 * b) / 255 > 0.6 ? colors.grey[900] : colors.white;
};

const escapeHtml = (s: string): string =>
  s.replace(/[&<>"']/g, (c) => (
    { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c] as string
  ));

const formatAddress = (d: Deal): string =>
  [d.street_address, d.city, d.state, d.zip_code].filter((p) => p && p.trim()).join(', ');

// --------------------------------------------------------------------------- //
// Imperative marker + cluster layer (markercluster has no react-leaflet v5
// wrapper; we build it on the live map instance).
// --------------------------------------------------------------------------- //
interface MarkerSpec { id: string; lat: number; lng: number; color: string; popupHtml: string; }

const DealLayer: React.FC<{ markers: MarkerSpec[] }> = ({ markers }) => {
  const map = useMap();
  const navigate = useNavigate();

  // One delegated click handler for "View deal" buttons inside popups.
  useEffect(() => {
    const container = map.getContainer();
    const onClick = (e: Event) => {
      const target = (e.target as HTMLElement)?.closest('.deal-map-view') as HTMLElement | null;
      if (target?.dataset.id) {
        e.preventDefault();
        navigate(`/models/${target.dataset.id}`);
      }
    };
    container.addEventListener('click', onClick);
    return () => container.removeEventListener('click', onClick);
  }, [map, navigate]);

  useEffect(() => {
    const group = (L as any).markerClusterGroup({ chunkedLoading: true, maxClusterRadius: 50 });
    markers.forEach((m) => {
      const marker = L.circleMarker([m.lat, m.lng], {
        radius: 9, color: '#ffffff', weight: 2, fillColor: m.color, fillOpacity: 0.95,
      });
      marker.bindPopup(m.popupHtml, { maxWidth: 260, minWidth: 180 });
      group.addLayer(marker);
    });
    map.addLayer(group);

    if (markers.length > 0) {
      try {
        map.fitBounds(group.getBounds(), { padding: [48, 48], maxZoom: 14 });
      } catch { /* getBounds can throw if empty — ignore */ }
    }
    return () => { map.removeLayer(group); };
  }, [map, markers]);

  return null;
};

// --------------------------------------------------------------------------- //
// Page
// --------------------------------------------------------------------------- //
const DealMap: React.FC = () => {
  const navigate = useNavigate();
  const { getAccessTokenSilently } = useAuth0();
  const { user } = useUser();

  const [deals, setDeals] = useState<Deal[]>([]);
  const [stageDefs, setStageDefs] = useState<StageDef[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedStages, setSelectedStages] = useState<Set<string>>(new Set());
  const [selectedTypes, setSelectedTypes] = useState<Set<string>>(new Set());
  const [drawerOpen, setDrawerOpen] = useState(false);
  const didFetch = useRef(false);

  const fetchAll = useCallback(async () => {
    if (!user?.id) return;
    setLoading(true);
    setError(null);
    try {
      const token = await getAccessTokenSilently({
        authorizationParams: { audience: process.env.REACT_APP_AUTH0_AUDIENCE },
      });
      const headers = { Authorization: `Bearer ${token}` };

      const dealsResp = await fetch(`${BACKEND_URL}/api/user_models?user_id=${user.id}`, {
        method: 'GET', headers, credentials: 'include',
      });
      if (!dealsResp.ok) throw new Error(`Could not load deals (${dealsResp.status})`);
      const dealsJson: Deal[] = await dealsResp.json();
      setDeals(Array.isArray(dealsJson) ? dealsJson.filter((d) => d.active !== false) : []);

      // Stage definitions: prefer Track A's endpoint when present, else the
      // read-only fallback exposed by the analytics blueprint. Either failing
      // is non-fatal — the map falls back to deriving stages from deal tags.
      const stages = await loadStageDefs(headers);
      setStageDefs(stages);
    } catch (e: any) {
      setError(e?.message || 'Could not load the map.');
    } finally {
      setLoading(false);
    }
  }, [user, getAccessTokenSilently]);

  const loadStageDefs = async (headers: Record<string, string>): Promise<StageDef[]> => {
    const tryUrl = async (url: string): Promise<StageDef[] | null> => {
      try {
        const r = await fetch(url, { method: 'GET', headers, credentials: 'include' });
        if (!r.ok) return null;
        const json = await r.json();
        const rows = Array.isArray(json) ? json : json?.tag_definitions;
        if (!Array.isArray(rows)) return null;
        return rows
          .filter((d: any) => d.is_pipeline_stage !== false && (d.status ?? 'active') === 'active')
          .map((d: any) => ({ name: d.name, color: d.color ?? null, sort_order: d.sort_order ?? 0 }));
      } catch { return null; }
    };
    return (await tryUrl(`${BACKEND_URL}/api/tag_definitions`))
      ?? (await tryUrl(`${BACKEND_URL}/api/pipeline/stages`))
      ?? [];
  };

  useEffect(() => {
    if (didFetch.current) return;
    if (user?.id) { didFetch.current = true; fetchAll(); }
  }, [user, fetchAll]);

  // ---- derive each deal's current stage (name + color) ----
  const defByName = useMemo(() => {
    const m = new Map<string, StageDef>();
    stageDefs.forEach((d) => m.set(d.name.toLowerCase(), d));
    return m;
  }, [stageDefs]);

  const stageOf = useCallback((deal: Deal): { name: string; color: string } | null => {
    const active = deal.model_tags.filter((t) => t.status === 'active');
    if (active.length === 0) return null;
    if (stageDefs.length > 0) {
      // current stage = active tag matching a pipeline stage with highest sort_order
      let best: StageDef | null = null;
      for (const t of active) {
        const def = defByName.get(t.tag_name.toLowerCase());
        if (def && (best === null || def.sort_order > best.sort_order)) best = def;
      }
      return best ? { name: best.name, color: best.color || hashColor(best.name) } : null;
    }
    // No definitions available: use the most recently applied active tag.
    const latest = [...active].sort((a, b) => (b.created_at || '').localeCompare(a.created_at || ''))[0];
    return { name: latest.tag_name, color: latest.tag_color || hashColor(latest.tag_name) };
  }, [stageDefs, defByName]);

  const stageById = useMemo(() => {
    const m = new Map<string, { name: string; color: string } | null>();
    deals.forEach((d) => m.set(d.id, stageOf(d)));
    return m;
  }, [deals, stageOf]);

  const geocoded = useMemo(
    () => deals.filter((d) => typeof d.latitude === 'number' && typeof d.longitude === 'number'),
    [deals],
  );
  const unlocated = useMemo(
    () => deals.filter((d) => typeof d.latitude !== 'number' || typeof d.longitude !== 'number'),
    [deals],
  );

  // ---- filter chip option lists ----
  const stageOptions = useMemo(() => {
    const opts: { key: string; label: string; color: string }[] = [];
    if (stageDefs.length > 0) {
      stageDefs.forEach((d) => opts.push({ key: d.name, label: d.name, color: d.color || hashColor(d.name) }));
    } else {
      const seen = new Map<string, string>();
      geocoded.forEach((d) => { const s = stageById.get(d.id); if (s && !seen.has(s.name)) seen.set(s.name, s.color); });
      seen.forEach((color, name) => opts.push({ key: name, label: name, color }));
    }
    if (geocoded.some((d) => !stageById.get(d.id))) opts.push({ key: UNTAGGED_KEY, label: 'Untagged', color: GRAY });
    return opts;
  }, [stageDefs, geocoded, stageById]);

  const typeOptions = useMemo(() => {
    const set = new Set<string>();
    geocoded.forEach((d) => { if (d.model_type) set.add(d.model_type); });
    return Array.from(set).sort();
  }, [geocoded]);

  // ---- visible markers ----
  const markers = useMemo<MarkerSpec[]>(() => {
    return geocoded
      .filter((d) => {
        const s = stageById.get(d.id);
        const stageKey = s ? s.name : UNTAGGED_KEY;
        const stageOk = selectedStages.size === 0 || selectedStages.has(stageKey);
        const typeOk = selectedTypes.size === 0 || selectedTypes.has(d.model_type || '');
        return stageOk && typeOk;
      })
      .map((d) => {
        const s = stageById.get(d.id);
        const color = s ? s.color : GRAY;
        const stageName = s ? s.name : 'Untagged';
        const irr = d.levered_irr ? `IRR ${escapeHtml(d.levered_irr)}` : '';
        const moic = d.levered_moic ? `MoIC ${escapeHtml(d.levered_moic)}` : '';
        const metrics = [irr, moic].filter(Boolean).join(' · ');
        const addr = formatAddress(d);
        const popupHtml =
          `<div class="deal-popup">` +
          `<div class="deal-popup-name">${escapeHtml(d.name || 'Untitled deal')}</div>` +
          (addr ? `<div class="deal-popup-addr">${escapeHtml(addr)}</div>` : '') +
          `<span class="deal-popup-stage" style="background:${color};color:${readableText(color)}">${escapeHtml(stageName)}</span>` +
          (d.model_type ? `<span class="deal-popup-type">${escapeHtml(d.model_type)}</span>` : '') +
          (metrics ? `<div class="deal-popup-metrics">${metrics}</div>` : '') +
          `<button class="deal-map-view" data-id="${escapeHtml(d.id)}">View deal →</button>` +
          `</div>`;
        return { id: d.id, lat: d.latitude as number, lng: d.longitude as number, color, popupHtml };
      });
  }, [geocoded, stageById, selectedStages, selectedTypes]);

  const toggle = (set: Set<string>, key: string, setter: (s: Set<string>) => void) => {
    const next = new Set(set);
    next.has(key) ? next.delete(key) : next.add(key);
    setter(next);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: { xs: 'calc(100vh - 56px)', md: '100vh' } }}>
        <ConstructionLoader />
      </Box>
    );
  }
  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" action={<Button color="inherit" size="small" onClick={() => { didFetch.current = false; fetchAll(); }}>Retry</Button>}>
          {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ position: 'relative', height: { xs: 'calc(100vh - 56px)', md: '100vh' }, width: '100%', overflow: 'hidden' }}>
      <GlobalStyles styles={{
        '.deal-popup-name': { fontWeight: 700, fontSize: '0.95rem', color: colors.grey[900], marginBottom: '2px' },
        '.deal-popup-addr': { fontSize: '0.78rem', color: colors.grey[600], marginBottom: '6px' },
        '.deal-popup-stage, .deal-popup-type': {
          display: 'inline-block', fontSize: '0.7rem', fontWeight: 600,
          padding: '2px 8px', borderRadius: '10px', marginRight: '4px', marginBottom: '6px',
        },
        '.deal-popup-type': { background: colors.grey[100], color: colors.grey[700] },
        '.deal-popup-metrics': { fontSize: '0.78rem', color: colors.grey[700], marginBottom: '8px' },
        '.deal-map-view': {
          display: 'block', width: '100%', cursor: 'pointer', border: 'none',
          background: colors.blue, color: '#fff', fontWeight: 600, fontSize: '0.8rem',
          padding: '6px 10px', borderRadius: '6px', fontFamily: 'inherit',
        },
        '.leaflet-popup-content': { margin: '10px 12px', maxWidth: '230px' },
      }} />

      {/* Filter overlay */}
      <Box sx={{
        position: 'absolute', top: 8, left: 8, right: 8, zIndex: 1000,
        bgcolor: 'rgba(255,255,255,0.94)', borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
        p: 1, display: 'flex', alignItems: 'center', gap: 1,
        flexWrap: { xs: 'nowrap', md: 'wrap' },
        overflowX: { xs: 'auto', md: 'visible' },
      }}>
        {stageOptions.map((o) => {
          const on = selectedStages.has(o.key);
          return (
            <Chip
              key={o.key}
              label={o.label}
              size="small"
              onClick={() => toggle(selectedStages, o.key, setSelectedStages)}
              variant={on ? 'filled' : 'outlined'}
              sx={{
                flexShrink: 0, fontWeight: 600,
                bgcolor: on ? o.color : 'transparent',
                color: on ? readableText(o.color) : colors.grey[700],
                borderColor: o.color,
                '& .MuiChip-label': { px: 1.2 },
                '&:hover': { bgcolor: on ? o.color : `${o.color}22` },
              }}
            />
          );
        })}
        {typeOptions.length > 0 && <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />}
        {typeOptions.map((t) => {
          const on = selectedTypes.has(t);
          return (
            <Chip
              key={t}
              label={t}
              size="small"
              variant={on ? 'filled' : 'outlined'}
              color={on ? 'primary' : 'default'}
              onClick={() => toggle(selectedTypes, t, setSelectedTypes)}
              sx={{ flexShrink: 0 }}
            />
          );
        })}
        <Box sx={{ flexGrow: 1 }} />
        {unlocated.length > 0 && (
          <Chip
            icon={<LocationOffIcon />}
            label={`${unlocated.length} without location`}
            size="small"
            onClick={() => setDrawerOpen(true)}
            sx={{ flexShrink: 0, bgcolor: colors.grey[100], fontWeight: 600 }}
          />
        )}
      </Box>

      <MapContainer center={US_CENTER} zoom={4} style={{ height: '100%', width: '100%' }} zoomControl={false}>
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        <DealLayer markers={markers} />
      </MapContainer>

      {markers.length === 0 && (
        <Box sx={{
          position: 'absolute', bottom: 16, left: '50%', transform: 'translateX(-50%)', zIndex: 1000,
          bgcolor: 'rgba(255,255,255,0.95)', px: 2, py: 1, borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
        }}>
          <Typography variant="body2" color="text.secondary">
            {geocoded.length === 0 ? 'No deals have map coordinates yet.' : 'No deals match the current filters.'}
          </Typography>
        </Box>
      )}

      {/* Without-location drawer */}
      <Drawer anchor="right" open={drawerOpen} onClose={() => setDrawerOpen(false)}>
        <Box sx={{ width: { xs: 290, sm: 340 }, p: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="h5" sx={{ fontWeight: 700 }}>Deals without a location</Typography>
            <IconButton size="small" onClick={() => setDrawerOpen(false)}><CloseIcon /></IconButton>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {unlocated.length} deal{unlocated.length === 1 ? '' : 's'} couldn’t be placed on the map. Add or fix the address to geocode them.
          </Typography>
          <Divider sx={{ mb: 1 }} />
          <List dense>
            {unlocated.map((d) => (
              <ListItem
                key={d.id}
                disableGutters
                secondaryAction={
                  <Button
                    size="small"
                    onClick={() => navigate(d.version_id ? `/edit-model/${d.version_id}` : `/models/${d.id}`)}
                  >
                    Edit
                  </Button>
                }
              >
                <ListItemText
                  primary={d.name || 'Untitled deal'}
                  secondary={formatAddress(d) || `No address · ${d.geocode_status ?? 'pending'}`}
                  primaryTypographyProps={{ fontWeight: 600, noWrap: true }}
                  secondaryTypographyProps={{ noWrap: true }}
                />
              </ListItem>
            ))}
          </List>
        </Box>
      </Drawer>
    </Box>
  );
};

export default DealMap;
